import { useEffect, useState, useCallback } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

import MDBox from "components/atoms/MDBox";
import MDTypography from "components/atoms/MDTypography";

import SidenavMainItem from "components/organisms/Sidenav/SidenavMainItem";
import SidenavList from "components/organisms/Sidenav/SidenavList";
import SidenavSubItem from "components/organisms/Sidenav/SidenavSubItem";

// Custom styles for the Sidenav
import SidenavRoot from "components/organisms/Sidenav/SidenavRoot";
import sidenavLogoLabel from "components/organisms/Sidenav/styles/sidenav";

import OutsideAlerter from "components/organisms/Sidenav/OutsideAlerter";

import {
  useMuiContext,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context/MuiContext";
import { AllowedTo } from "react-abac";

function Sidenav({ color, brand, brandName, links, ...rest }) {
  const location = useLocation();
  const { pathname } = location;
  const collapseName = pathname.split("/").slice(1)[0];
  const items = pathname.split("/").slice(1);
  const itemParentName = items[1];
  const itemName = items[items.length - 1];
  const [openCollapse, setOpenCollapse] = useState(collapseName);
  const [openNestedCollapse, setOpenNestedCollapse] = useState(itemParentName);
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, dispatch } =
    useMuiContext();

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  // A function that sets the mini state of the sidenav.
  const handleMiniSidenav = useCallback(() => {
    setMiniSidenav(dispatch, window.innerWidth < 1200);
    // setTransparentSidenav(
    //   dispatch,
    //   window.innerWidth < 1200 ? false : transparentSidenav
    // );
    setTransparentSidenav(dispatch, false);
    //setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    setWhiteSidenav(dispatch, false);
  }, [dispatch]);

  useEffect(() => {
    /**
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [handleMiniSidenav]);

  // Render all the nested collapse items from the links
  const renderNestedCollapse = (collapse) =>
    collapse.map(({ name, link, key, href, permissions }) => {
      let returnValue;

      if (href) {
        returnValue = (
          <Link
            key={key}
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavSubItem name={name} nested />
          </Link>
        );
      } else if (link) {
        returnValue = (
          <NavLink to={link} key={key} sx={{ textDecoration: "none" }}>
            <SidenavSubItem name={name} active={link === pathname} nested />
          </NavLink>
        );
      }

      return (
        <AllowedTo key={key} perform={permissions} no={() => <></>}>
          {returnValue}
        </AllowedTo>
      );
    });

  // Render the all the collpases from the links
  const renderCollapse = (collapses) =>
    collapses.map(({ name, collapse, link, href, key, permissions }) => {
      let returnValue;

      if (collapse) {
        returnValue = (
          <SidenavSubItem
            key={key}
            color={color}
            name={name}
            active={key === itemParentName ? "isParent" : false}
            open={openNestedCollapse === key}
            onClick={({ currentTarget }) =>
              openNestedCollapse === key &&
              currentTarget.classList.contains("MuiListItem-root")
                ? setOpenNestedCollapse(false)
                : setOpenNestedCollapse(key)
            }
          >
            {renderNestedCollapse(collapse)}
          </SidenavSubItem>
        );
      } else {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavSubItem
                color={color}
                name={name}
                active={key === itemName}
              />
            </Link>
          );
        } else if (link) {
          returnValue = (
            <NavLink to={link} key={key} sx={{ textDecoration: "none" }}>
              <SidenavSubItem
                color={color}
                name={name}
                active={link === pathname}
              />
            </NavLink>
          );
        }
      }
      return (
        <AllowedTo key={key} perform={permissions} no={() => <></>}>
          <SidenavList key={key}>{returnValue}</SidenavList>
        </AllowedTo>
      );
    });

  // Render all the links (All the visible items on the Sidenav)
  const renderLinks = links.map(
    ({
      type,
      name,
      icon,
      title,
      collapse,
      noCollapse,
      key,
      link,
      href,
      permissions,
    }) => {
      let returnValue;

      if (type === "collapse") {
        returnValue = (
          <SidenavMainItem
            key={key}
            name={name}
            icon={icon}
            active={key === collapseName}
            open={openCollapse === key}
            onClick={() =>
              openCollapse === key
                ? setOpenCollapse(false)
                : setOpenCollapse(key)
            }
          >
            {collapse ? renderCollapse(collapse) : null}
          </SidenavMainItem>
        );
      } else if (type === "title") {
        returnValue = (
          <MDTypography
            key={key}
            color={textColor}
            display="block"
            variant="caption"
            fontWeight="bold"
            textTransform="uppercase"
            pl={3}
            mt={2}
            mb={1}
            ml={1}
          >
            {title}
          </MDTypography>
        );
      } else if (type === "divider") {
        returnValue = (
          <Divider
            key={key}
            light={
              (!darkMode && !whiteSidenav && !transparentSidenav) ||
              (darkMode && !transparentSidenav && whiteSidenav)
            }
          />
        );
      } else {
        if (href) {
          returnValue = (
            <Link
              href={href}
              key={key}
              target="_blank"
              rel="noreferrer"
              sx={{ textDecoration: "none" }}
            >
              <SidenavMainItem
                name={name}
                icon={icon}
                active={key === collapseName}
              />
            </Link>
          );
        } else if (link) {
          returnValue = (
            <NavLink to={link} key={key} sx={{ textDecoration: "none" }}>
              <SidenavMainItem
                name={name}
                icon={icon}
                active={link === pathname}
              />
            </NavLink>
          );
        }
      }

      return (
        <AllowedTo key={key} perform={permissions} no={() => <></>}>
          {returnValue}
        </AllowedTo>
      );
    }
  );

  return (
    <OutsideAlerter closeSidenav={closeSidenav}>
      <SidenavRoot
        {...rest}
        variant="permanent"
        ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
      >
        <MDBox pt={3} pb={1} px={4} textAlign="center">
          <MDBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={closeSidenav}
            sx={{ cursor: "pointer" }}
          >
            <MDTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </MDTypography>
          </MDBox>
          <MDBox component={NavLink} to="/" display="flex" alignItems="center">
            {brand && (
              <MDBox component="img" src={brand} alt="Brand" width="2.5rem" />
            )}
            <MDBox
              width={!brandName && "100%"}
              sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
            >
              <MDTypography variant="h6" color={textColor}>
                {brandName}
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
        <List>{renderLinks}</List>
      </SidenavRoot>
    </OutsideAlerter>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
